import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, ExternalLink, MessageCircle, Search } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import useCopyToClipboard from '../hooks/useCopyToClipboard';
import { copyServerIp } from '../lib/copyServerIp';
import { NAV_LINKS } from '../config/nav';
import { RULES_MENU, RULES_SECTION_PARAM } from '../config/rules';
import { EXTERNAL } from '../config/links';
import '../styles/CommandPalette.css';

/**
 * Cmd/Ctrl+K jump-to. The visible trigger matters as much as the shortcut —
 * a keyboard-only palette is invisible to most visitors.
 */
const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const copy = useCopyToClipboard();

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key?.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((previous) => !previous);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Close first so the dialog is never left open behind a navigation.
  const run = (action) => {
    setOpen(false);
    action();
  };

  const openExternal = (url) => window.open(url, '_blank', 'noopener,noreferrer');

  return (
    <>
      <button
        type="button"
        className="palette-trigger"
        onClick={() => setOpen(true)}
        aria-label="Search the site"
      >
        <Search className="palette-trigger__icon" aria-hidden="true" />
        <span className="palette-trigger__label">Search</span>
        {/* <kbd className="palette-trigger__kbd">⌘K</kbd> */}
      </button>

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title="Search"
        description="Jump to a page or a rules section, or run an action"
      >
        <CommandInput placeholder="Search pages, rules and actions..." />
        <CommandList>
          <CommandEmpty>Nothing matched that.</CommandEmpty>

          <CommandGroup heading="Pages">
            {NAV_LINKS.map((link) => (
              // Values are prefixed because "Home" appears both as a page and
              // as a rules section, and cmdk keys items by value.
              <CommandItem
                key={link.to}
                value={`page ${link.label}`}
                onSelect={() => run(() => navigate(link.to))}
              >
                {link.label}
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandSeparator />

          <CommandGroup heading="Rules">
            {RULES_MENU.map((section) => (
              <CommandItem
                key={section.key}
                value={`rules ${section.label}`}
                onSelect={() =>
                  run(() =>
                    navigate(`/rules?${RULES_SECTION_PARAM}=${section.key}`)
                  )
                }
              >
                {section.label}
              </CommandItem>
            ))}
          </CommandGroup>

          <CommandSeparator />

          <CommandGroup heading="Actions">
            <CommandItem
              value="action copy server ip address"
              onSelect={() => run(() => copyServerIp(copy))}
            >
              <Copy aria-hidden="true" />
              Copy server IP
            </CommandItem>
            <CommandItem
              value="action join discord"
              onSelect={() => run(() => openExternal(EXTERNAL.discord))}
            >
              <MessageCircle aria-hidden="true" />
              Join the Discord
            </CommandItem>
            <CommandItem
              value="action visit store shop"
              onSelect={() => run(() => openExternal(EXTERNAL.store))}
            >
              <ExternalLink aria-hidden="true" />
              Visit the store
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
};

export default CommandPalette;
